import { useState } from "react";

export type WizardStepConfig = {
  name: string;
  dataKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.FC<any>;
};

export interface StepProps {
  onNext: (nextStepName?: string) => void;
  onPrevious?: () => void;
  onClose?: () => void;
}
export interface FormStepProps<T> extends StepProps {
  data: T | undefined;
  formData?: Record<string, object>;
  updateData: (data: T) => void;
}

interface WizardProps {
  steps: WizardStepConfig[];
  onClose?: () => void;
}

export default function Wizard({ steps, onClose }: WizardProps) {
  const [currentStepName, setCurrentStepName] = useState(steps[0].name);
  const [history, setHistory] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, object>>({});

  const handleNext = (nextStepName?: string) => {
    nextStepName = typeof nextStepName === "string" ? nextStepName : undefined;

    if (!nextStepName) {
      const currentIndex = steps.findIndex((s) => s.name === currentStepName);
      const nextStep = steps[currentIndex + 1];
      if (!nextStep) {
        onClose?.();
        return;
      }
      nextStepName = nextStep.name;
    }

    if (stepMap[nextStepName]) {
      setHistory((prev) => [...prev, currentStepName]);
      setCurrentStepName(nextStepName);
    }
  };

  const handlePrevious = () => {
    if (history.length === 0) {
      return;
    }
    const lastStep = history.at(-1);
    if (lastStep) {
      setCurrentStepName(lastStep);
      setHistory(history.slice(0, -1));
    }
  };

  const updateStepData = (key: string, data: object) => {
    setFormData((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...data },
    }));
  };

  const stepMap = Object.fromEntries(steps.map((step) => [step.name, step]));
  const currentStep = stepMap[currentStepName];

  const CurrentStepComponent = currentStep.component;
  const currentStepDataKey = currentStep.dataKey;

  return (
    <CurrentStepComponent
      onNext={handleNext}
      onPrevious={handlePrevious}
      onClose={onClose}
      data={currentStepDataKey ? formData[currentStepDataKey] : undefined}
      updateData={
        currentStepDataKey
          ? (data: object) => updateStepData(currentStepDataKey, data)
          : undefined
      }
      formData={formData}
    />
  );
}
